import React, { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { Building2, Pickaxe } from 'lucide-react';
import StandardAppLayout from 'src/shared/components/layout/standard-app-layout/StandardAppLayout';
import useFeatureFlag from 'src/shared/hooks/useFeatureFlag';
import useCustomer from 'src/app/customers/hooks/useCustomer';
import useAppNavigation from 'src/shared/hooks/useAppNavigation';
import Loader from 'src/shared/components/loader/Loader';
import PageNotFound from 'src/shared/components/page-not-found/PageNotFound';
import RiskScoreCard from 'src/shared/components/risk-score-card/RiskScoreCard';
import ImpactsAndDependenciesInfoBlock from 'src/shared/components/impacts-and-dependencies-info-block/ImpactsAndDependenciesInfoBlock';
import RankCard from 'src/shared/components/rank-card/RankCard';
import { useGetSiteNatureRiskOverviewQuery } from 'src/app/site-nature-risk/state/api/siteNatureRiskEngineSlice';
import { useGetRiskMaterialityFactorsQuery } from 'src/shared/state/api-slices/riskMaterialityCmsSlice';
import RiskDriversSection from 'src/shared/components/risk-drivers/RiskDriversSection';

const FEATURE_ID = 'risk-insights';

const SiteNatureRiskImpactsAndDependencies = () => {
    const { siteId } = useParams<{ siteId: string }>();
    const { currentCustomerId } = useCustomer();
    const navigation = useAppNavigation();
    const { enabled, loading } = useFeatureFlag({ featureId: FEATURE_ID });

    const { data, isLoading } = useGetSiteNatureRiskOverviewQuery(
        { organisationBusinessCentralId: currentCustomerId, siteId: siteId! },
        { skip: !currentCustomerId || !siteId }
    );

    const { data: riskMaterialityFactors = [] } = useGetRiskMaterialityFactorsQuery();

    const site = data?.site;
    const materialityBreakdown = site?.materialityBreakdown ?? [];

    const cmsDescriptionMap = useMemo(
        () => Object.fromEntries(riskMaterialityFactors.map(f => [f.sourceKey, f.description])),
        [riskMaterialityFactors]
    );

    const dependencyItems = useMemo(
        () =>
            materialityBreakdown
                .filter(m => m.materialityType === 'Dependency')
                .map(m => ({ name: m.name, score: m.score, description: cmsDescriptionMap[m.sourceKey] ?? null })),
        [materialityBreakdown, cmsDescriptionMap]
    );

    const impactItems = useMemo(
        () =>
            materialityBreakdown
                .filter(m => m.materialityType === 'Impact')
                .map(m => ({ name: m.name, score: m.score, description: cmsDescriptionMap[m.sourceKey] ?? null })),
        [materialityBreakdown, cmsDescriptionMap]
    );

    if (loading) return <Loader />;
    if (!enabled) return <PageNotFound />;

    const riskSummary = site?.riskSummary ?? null;
    const dependencyRank = site?.dependencyRank ?? null;
    const impactRank = site?.impactRank ?? null;

    const breadcrumbs = [{ label: 'Nature Risk', path: navigation.globalNatureRiskOverviewPath }, { label: site?.name ?? '' }];

    return (
        <StandardAppLayout
            title={site?.name ?? ''}
            subTitle='Impacts & Dependencies'
            breadcrumbs={breadcrumbs}
            stickyHeader={false}
            mainContent={
                <div
                    className='flex flex-col w-full gap-6 flex-1 min-h-0 pt-1 mb-6'
                    data-testid='site-nature-risk-impacts-and-dependencies-main'
                >
                    <div className='grid grid-cols-2 gap-4'>
                        {/* Dependencies column */}
                        <div className='flex flex-col gap-1'>
                            <ImpactsAndDependenciesInfoBlock
                                title='Dependencies'
                                icon={Building2}
                                slug='dependencies-te'
                                description='Evaluates reliance on ecosystem services such as water provision, climate regulation, and pollination that are critical for business operations and may be at risk due to environmental degradation.'
                            />
                            <div className='flex flex-row gap-1'>
                                <div className='flex-1 flex flex-col'>
                                    <RiskScoreCard
                                        title='Dependencies'
                                        value={riskSummary?.operationalDependencies ?? null}
                                        scoreType='Risk'
                                        isLoading={isLoading}
                                        size='md'
                                        slug='dependencies'
                                    />
                                </div>
                                <RankCard
                                    title='Dependencies Rank'
                                    rank={dependencyRank !== null ? `#${dependencyRank.rank}` : null}
                                    total={dependencyRank?.total ?? null}
                                    isLoading={isLoading}
                                    slug='high-dependency-sites'
                                    className='flex-1'
                                />
                            </div>
                        </div>

                        {/* Impact on Nature column */}
                        <div className='flex flex-col gap-1'>
                            <ImpactsAndDependenciesInfoBlock
                                title='Impact on Nature'
                                icon={Pickaxe}
                                slug='impact-on-nature-te'
                                description='Measures the negative effects of business operations on nature, including pollution, habitat destruction, resource depletion, and greenhouse gas emissions that contribute to ecosystem degradation.'
                            />
                            <div className='flex flex-row gap-1'>
                                <div className='flex-1 flex flex-col'>
                                    <RiskScoreCard
                                        title='Impact on Nature'
                                        value={riskSummary?.impactsOnNature ?? null}
                                        scoreType='Risk'
                                        isLoading={isLoading}
                                        size='md'
                                        slug='impact-on-nature'
                                    />
                                </div>
                                <RankCard
                                    title='Impact on Nature Rank'
                                    rank={impactRank !== null ? `#${impactRank.rank}` : null}
                                    total={impactRank?.total ?? null}
                                    isLoading={isLoading}
                                    slug='high-impact-on-nature-sites'
                                    className='flex-1'
                                />
                            </div>
                        </div>
                    </div>

                    <RiskDriversSection dependencyItems={dependencyItems} impactItems={impactItems} isLoading={isLoading} />
                </div>
            }
        />
    );
};

export default SiteNatureRiskImpactsAndDependencies;
